/**
 * Timeline Legend Component
 * Shows employee colors and layer information
 */

import React from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'

import { EmployeeLegendItem } from '../../../../types/timeline'
import { LAYER_COLORS } from '../utils/transformAssignments'

interface TimelineLegendProps {
    employees: EmployeeLegendItem[]
    showLayers?: boolean
}

const TimelineLegend: React.FC<TimelineLegendProps> = ({
    employees,
    showLayers = false,
}) => {
    const { t: translate } = useTranslation('oncall')

    if (employees.length === 0) {
        return null
    }

    return (
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex flex-wrap items-center gap-4">
                {/* Employee legend */}
                <div className="flex flex-wrap items-center gap-4">
                    <Typogaphy
                        size="small"
                        className="text-gray-500 font-medium text-xs capitalize"
                    >
                        {translate('legend')}:
                    </Typogaphy>
                    {employees?.map((employee) => (
                        <div
                            key={employee.employeeId}
                            className="flex items-center gap-2"
                        >
                            <span
                                className="w-2 h-2 rounded-[2px]"
                                style={{ backgroundColor: employee.color }}
                            />
                            <Typogaphy
                                size="small"
                                className="text-gray-700 text-xs capitalize"
                            >
                                {employee.employeeName}
                            </Typogaphy>
                        </div>
                    ))}
                </div>

                {/* Layer legend (optional) - only Scheduled and Swap */}
                {showLayers && (
                    <>
                        <div className="h-4 w-px bg-gray-300" />
                        <div className="flex items-center gap-3">
                            <Typogaphy
                                size="small"
                                className="text-gray-500 font-medium"
                            >
                                {translate('layers')}:
                            </Typogaphy>
                            <div className="flex items-center gap-2">
                                <span
                                    className="w-3 h-3 rounded-sm"
                                    style={{
                                        backgroundColor: LAYER_COLORS.base,
                                    }}
                                />
                                <Typogaphy
                                    size="small"
                                    className="text-gray-600"
                                >
                                    {translate('scheduled')}
                                </Typogaphy>
                            </div>
                            <div className="flex items-center gap-2">
                                <span
                                    className="w-3 h-3 rounded-sm"
                                    style={{
                                        backgroundColor: LAYER_COLORS.override,
                                    }}
                                />
                                <Typogaphy
                                    size="small"
                                    className="text-gray-600"
                                >
                                    {translate('swap')}
                                </Typogaphy>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

export default TimelineLegend
